import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import "./Header.css";
import { Link, NavLink, useLocation } from "react-router-dom";
import SeoHeader from "../seoHeader/SeoHeader";
import ThemeToggle from "../themeToggle/ThemeToggle";
import CommandMenu from "../commandMenu/CommandMenu";

const links = [
  { to: "/", label: "Home" },
  { to: "/education", label: "Education" },
  { to: "/experience", label: "Experience" },
  { to: "/projects", label: "Projects" },
];

const isActive = (path, to) =>
  to === "/" ? path === "/" || path === "/home" : path.startsWith(to);

const isMac =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

export default function Header() {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [hover, setHover] = useState(null);
  const [pill, setPill] = useState({ x: 0, w: 0, show: false });
  const itemRefs = useRef([]);
  const barRef = useRef(null);
  const lastY = useRef(0);

  const activeIndex = links.findIndex((l) => isActive(pathname, l.to));
  const shown = hover !== null ? hover : activeIndex;

  // sliding highlight follows the hovered / active link
  useLayoutEffect(() => {
    const place = () => {
      const el = itemRefs.current[shown];
      if (!el) return setPill((p) => ({ ...p, show: false }));
      setPill({ x: el.offsetLeft, w: el.offsetWidth, show: true });
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [shown]);

  // compact when scrolled; hide on scroll down, reveal on scroll up
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 24);
      const dy = y - lastY.current;
      if (Math.abs(dy) > 6) {
        setHidden(dy > 0 && y > 240);
        lastY.current = y;
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Ctrl/Cmd+K opens the command menu from anywhere
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // lock page scroll while a full-screen layer is open
  const locked = open || cmdOpen;
  useEffect(() => {
    if (!locked) return undefined;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [locked]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  useEffect(() => setOpen(false), [pathname]);

  // cursor spotlight across the bar
  const onBarMove = (e) => {
    const bar = barRef.current;
    if (!bar) return;
    const r = bar.getBoundingClientRect();
    bar.style.setProperty("--mx", `${e.clientX - r.left}px`);
    bar.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  const cls = [
    "site-header",
    scrolled && "scrolled",
    hidden && !open && !cmdOpen && "hidden",
    open && "open",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <SeoHeader />
      <header className={cls}>
        <div className="bar" ref={barRef} onPointerMove={onBarMove}>
          <button
            type="button"
            className="cmd-btn"
            onClick={() => setCmdOpen(true)}
            aria-label="Open quick navigation"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3.5-3.5" />
            </svg>
            <span className="cmd-btn-text">Jump to…</span>
            <span className="cmd-key">
              <kbd>{isMac ? "⌘" : "Ctrl"}</kbd>
              <kbd>K</kbd>
            </span>
          </button>

          <nav className="nav" aria-label="Primary" onMouseLeave={() => setHover(null)}>
            <span
              className="nav-pill"
              aria-hidden="true"
              style={{
                width: pill.w,
                transform: `translateX(${pill.x}px)`,
                opacity: pill.show ? 1 : 0,
              }}
            />
            {links.map((l, i) => (
              <NavLink
                key={l.to}
                to={l.to}
                innerRef={(el) => (itemRefs.current[i] = el)}
                className={`nav-link${shown === i ? " lit" : ""}`}
                isActive={() => isActive(pathname, l.to)}
                activeClassName="active"
                onMouseEnter={() => setHover(i)}
                onFocus={() => setHover(i)}
                onBlur={() => setHover(null)}
              >
                <span className="roll">
                  <span>{l.label}</span>
                  <span aria-hidden="true">{l.label}</span>
                </span>
              </NavLink>
            ))}
          </nav>

          <div className="actions">
            <ThemeToggle />
            <Link to="/contact" className="btn btn-primary btn-sm menu-cta">
              <span className="cta-label">Let's talk</span>
              <span className="cta-arrow" aria-hidden="true">→</span>
            </Link>
            <button
              type="button"
              className="burger btn-icon"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              onClick={() => setOpen((o) => !o)}
            >
              <span />
              <span />
            </button>
          </div>
        </div>

        <div className="mobile-panel" aria-hidden={!open}>
          <nav aria-label="Mobile">
            {[...links, { to: "/contact", label: "Contact" }].map((l, i) => (
              <Link
                key={l.to}
                to={l.to}
                className={`mobile-link${isActive(pathname, l.to) ? " active" : ""}`}
                style={{ transitionDelay: open ? `${80 + i * 60}ms` : "0ms" }}
                tabIndex={open ? 0 : -1}
              >
                <em>{String(i + 1).padStart(2, "0")}</em>
                {l.label}
              </Link>
            ))}
          </nav>
          <p className="mobile-foot">Full-stack · Mobile · Cloud</p>
        </div>

        <CommandMenu open={cmdOpen} onClose={() => setCmdOpen(false)} />
      </header>
    </>
  );
}
